import XDate from 'xdate'

import {
  AvailabilityHistoryEntry,
  ImportedListing,
  ImportSourceSite,
} from '../../_types/imported-listing'
import { formatRelativeTime } from '../../_utilities/relative-time'
import { PriceChangeSummary } from './types'

/**
 * Picks the renter-facing label for a known source site.
 *
 * @param {ImportSourceSite} site - the enum value
 * @returns {string} the human label that goes on the badge / breadcrumb
 */
const formatSourceSiteLabel = (site: ImportSourceSite): string => {
  if (site === 'rentals.ca') {
    return 'rentals.ca'
  }

  if (site === 'kijiji.ca') {
    return 'Kijiji'
  }

  if (site === 'apartments.com') {
    return 'apartments.com'
  }

  if (site === 'padmapper.com') {
    return 'PadMapper'
  }

  if (site === 'realtor.ca') {
    return 'realtor.ca'
  }

  if (site === 'zumper.com') {
    return 'Zumper'
  }

  return 'Imported listing'
}

/**
 * Joins the secondary meta line: bedrooms / bathrooms / property type / city.
 *
 * @param {ImportedListing} listing - the listing record
 * @returns {string[]} the meta parts in render order
 */
const buildMetaParts = (listing: ImportedListing): string[] => {
  const parts: string[] = []

  if (listing.bedrooms !== null) {
    if (listing.bedrooms === 0) {
      parts.push('Studio')
    } else {
      parts.push(`${listing.bedrooms} bed${listing.bedrooms === 1 ? '' : 's'}`)
    }
  }

  if (listing.bathrooms !== null) {
    parts.push(`${listing.bathrooms} bath${listing.bathrooms === 1 ? '' : 's'}`)
  }

  if (listing.propertyType !== null) {
    const capitalised = `${listing.propertyType[0].toUpperCase()}${listing.propertyType.slice(1)}`
    parts.push(capitalised)
  }

  if (listing.city !== '') {
    parts.push(listing.city)
  }

  return parts
}

/**
 * Computes a summary of the price change from first observation to most
 * recent observation. Used for the headline price card on the detail
 * page (eg "Down $100 (-4%) since first checked").
 *
 * @param {ImportedListing} listing - the listing record
 * @returns {PriceChangeSummary | null} the summary, or null when there's not enough data
 */
const buildPriceChangeSummary = (
  listing: ImportedListing,
): PriceChangeSummary | null => {
  const validEntries = listing.priceHistory.filter(
    entry => typeof entry.priceMonthly === 'number',
  )

  if (validEntries.length < 2) {
    return null
  }

  const firstPrice = validEntries[0].priceMonthly as number
  const lastPrice = validEntries[validEntries.length - 1].priceMonthly as number

  if (firstPrice === lastPrice) {
    return {
      changedBy: 0,
      changedFraction: 0,
      isUp: false,
      isFlat: true,
    }
  }

  const delta = lastPrice - firstPrice

  return {
    changedBy: Math.abs(delta),
    changedFraction: Math.abs(delta) / firstPrice,
    isUp: delta > 0,
    isFlat: false,
  }
}

/**
 * Renders a renter-facing relative timestamp (3h ago, Yesterday, MMM d).
 *
 * @param {number} observedAt - the millis timestamp to format
 * @returns {string} the relative-time string
 */
const formatObservedAt = (observedAt: number): string => {
  return formatRelativeTime(observedAt)
}

/**
 * Picks the renter-facing label for an availability transition.
 *
 * @param {AvailabilityHistoryEntry} entry - the availability entry
 * @returns {string} the renter-friendly label for the transition
 */
const formatAvailabilityLabel = (entry: AvailabilityHistoryEntry): string => {
  if (entry.isAvailable === true) {
    if (entry.availableDate !== null) {
      const dateLabel = new XDate(entry.availableDate).toString('MMM d')
      return `Available — move-in ${dateLabel}`
    }

    return 'Available'
  }

  if (entry.isAvailable === false) {
    return 'Marked unavailable'
  }

  return 'Availability unknown'
}

/**
 * Compresses the availability history into a deduplicated change-only
 * list — consecutive same-state entries collapse into one. Useful so the
 * timeline doesn't show 14 "still available" rows.
 *
 * @param {AvailabilityHistoryEntry[]} entries - the raw history entries
 * @returns {AvailabilityHistoryEntry[]} the compressed timeline
 */
const compressAvailabilityHistory = (
  entries: AvailabilityHistoryEntry[],
): AvailabilityHistoryEntry[] => {
  const compressed: AvailabilityHistoryEntry[] = []
  let lastState: boolean | null | undefined = undefined

  for (const entry of entries) {
    if (entry.isAvailable !== lastState) {
      compressed.push(entry)
      lastState = entry.isAvailable
    }
  }

  return compressed.reverse()
}

const detailContainerMotion = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.05 },
  },
}

const detailItemMotion = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.32 } },
}

export {
  buildMetaParts,
  buildPriceChangeSummary,
  compressAvailabilityHistory,
  detailContainerMotion,
  detailItemMotion,
  formatAvailabilityLabel,
  formatObservedAt,
  formatSourceSiteLabel,
}
